/**
 * Properties panel — context-sensitive field editing
 * Shows editable fields when a project item or template is selected
 */
import { selectionStore } from '../stores/selectionStore';
import { projectStore } from '../stores/projectStore';
import { propertiesStore, type PropertiesTab } from '../stores/propertiesStore';
import { monitorStore } from '../stores/monitorStore';
import { zoneStore, ZONE_PRESETS, renderZoneDiagram } from '../stores/zoneStore';
import { templateRegistry } from '../engine/templateRegistry';
import { eventBus } from '../engine/eventBus';
import { openFileDialog } from './fileDialog';
import { PATHS } from '../data/fakeFileSystem';
import type { TemplateField, ProjectItem } from '@graphicsim/shared-types';
import { hasScoreBirdConnection, renderScorebirdTab, initScorebirdPanel } from './scorebirdPanel';
import { getConnection } from '../engine/scorebirdPoller';
import { getPendingSport } from './dataControllerMenu';

let panelBody: HTMLElement;
let showScorebirdTab = false;

/** Programmatically switch Properties to the ScoreBird Settings tab */
export function showScorebirdSettings(): void {
  showScorebirdTab = true;
  render();
}

export function initProperties(): void {
  panelBody = document.querySelector('.properties .panel-body')!;
  initScorebirdPanel();

  // Listen for selection changes
  selectionStore.subscribe(() => {
    const sel = selectionStore.getState().current;
    if (sel.type === 'projectItem') {
      propertiesStore.getState().setEditingItem(sel.itemId);
    } else {
      propertiesStore.getState().setEditingItem(null);
    }
    render();
  });

  // Listen for EXTERNAL field updates to re-render (e.g. data linker)
  // Skip re-render when the user is actively editing inside this panel
  eventBus.on('project:itemUpdated', () => {
    if (!panelBody.contains(document.activeElement)) {
      render();
    }
  });

  // Re-render on zone preset change (zone names may change in Transform tab)
  eventBus.on('zone:presetChanged', () => render());

  // Subscribe to properties store for tab changes
  propertiesStore.subscribe(render);

  render();
}

function render(): void {
  const { editingItemId, activeTab } = propertiesStore.getState();

  if (!editingItemId) {
    // Check if a template is selected (not yet added to project)
    const sel = selectionStore.getState().current;
    if (sel.type === 'template') {
      renderTemplatePreview(sel.templateId);
      return;
    }
    panelBody.innerHTML = `<div class="props-empty">No item selected</div>`;
    return;
  }

  const item = projectStore.getState().getItem(editingItemId);
  if (!item) {
    panelBody.innerHTML = `<div class="props-empty">Item not found</div>`;
    return;
  }

  // Media items (no templateId) — show only Transform tab
  if (!item.templateId) {
    panelBody.innerHTML = `
      <div class="props-tabs">
        <button class="props-tab active" data-tab="transform">Transform</button>
      </div>
      <div class="props-header">
        <span class="props-item-name">${item.name}</span>
      </div>
      <div class="props-content">
        ${renderTransform(editingItemId)}
      </div>
    `;
    attachHandlers(editingItemId);
    return;
  }

  const template = templateRegistry.getTemplate(item.templateId);
  if (!template) {
    panelBody.innerHTML = `<div class="props-empty">Unknown template</div>`;
    return;
  }

  // Check for ScoreBird connection or pending sport selection
  const sbConn = getConnection(editingItemId);
  const pendingSport = getPendingSport(editingItemId);
  const hasSB = !!sbConn || !!pendingSport;
  const sbSport = sbConn?.sport || pendingSport || 'football';

  // If showing ScoreBird tab, render that instead
  if (showScorebirdTab && hasSB) {
    const sbTabLabel = `Scorebird: ${sbSport.charAt(0).toUpperCase() + sbSport.slice(1)}.Instance 1`;
    panelBody.innerHTML = `
      <div class="props-panel-tabs">
        <button class="props-panel-tab" data-panel-tab="properties">Properties</button>
        <button class="props-panel-tab active" data-panel-tab="scorebird">${sbTabLabel}</button>
      </div>
      <div class="sb-tab-content"></div>
    `;
    // Attach panel tab handlers
    panelBody.querySelectorAll<HTMLButtonElement>('.props-panel-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        showScorebirdTab = btn.dataset.panelTab === 'scorebird';
        render();
      });
    });
    const sbContainer = panelBody.querySelector<HTMLElement>('.sb-tab-content')!;
    renderScorebirdTab(editingItemId, sbContainer);
    return;
  }

  // Render tabs + fields — matches NEVCO Properties panel layout
  panelBody.innerHTML = `
    ${hasSB ? `<div class="props-panel-tabs">
      <button class="props-panel-tab active" data-panel-tab="properties">Properties</button>
      <button class="props-panel-tab" data-panel-tab="scorebird">Scorebird: ${sbSport.charAt(0).toUpperCase() + sbSport.slice(1)}.Instance 1</button>
    </div>` : ''}
    <div class="props-tabs">
      <button class="props-tab${activeTab === 'values' ? ' active' : ''}" data-tab="values">Live Values</button>
      <button class="props-tab${activeTab === 'grid' ? ' active' : ''}" data-tab="grid">Values Grid</button>
      <button class="props-tab${activeTab === 'transform' ? ' active' : ''}" data-tab="transform">Transform</button>
    </div>
    <div class="props-header">
      <span class="props-item-name">${item.name}</span>
      <div class="props-header-actions">
        <span class="props-show-all">Show All Variables</span>
        <span class="props-show-all-dot">&#x25CF;</span>
        <button class="props-header-btn">Add to Values Grid</button>
        <button class="props-header-btn props-auto-update active">Auto Update</button>
      </div>
    </div>
    <div class="props-content">
      ${activeTab === 'values' ? renderFieldEditors(template.fields, item.fieldValues, editingItemId) : ''}
      ${activeTab === 'grid' ? renderValuesGrid(template.fields, item.fieldValues, editingItemId) : ''}
      ${activeTab === 'transform' ? renderTransform(editingItemId) : ''}
    </div>
  `;

  // Attach panel tab handlers (Properties / Scorebird toggle)
  panelBody.querySelectorAll<HTMLButtonElement>('.props-panel-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      showScorebirdTab = btn.dataset.panelTab === 'scorebird';
      render();
    });
  });

  attachHandlers(editingItemId);
}

function renderTemplatePreview(templateId: string): void {
  const template = templateRegistry.getTemplate(templateId);
  if (!template) return;
  panelBody.innerHTML = `
    <div class="props-header">
      <span class="props-item-name">${template.name}</span>
      <span class="props-template-name">${template.category}</span>
    </div>
    <div class="props-content">
      <div class="props-preview-hint">Add to project to edit fields</div>
      <div class="props-field-list">
        ${template.fields.map((f) => `
          <div class="props-field-row readonly">
            <label class="props-label">${f.label}</label>
            <span class="props-value-preview">${f.defaultValue}</span>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderFieldEditors(fields: TemplateField[], values: Record<string, string>, itemId: string): string {
  // Flat list — no group headers, matching real NEVCO Properties panel
  return `<div class="props-field-list">
    ${fields.map((f) => renderField(f, values[f.id] ?? f.defaultValue, itemId)).join('')}
  </div>`;
}

function renderField(field: TemplateField, value: string, itemId: string): string {
  const isColor = field.type === 'color' || field.type === 'gradient';
  const labelClass = isColor ? 'props-label props-label-color' : 'props-label';
  let input = '';

  switch (field.type) {
    case 'text':
    case 'number':
      input = `<input type="${field.type === 'number' ? 'number' : 'text'}" class="props-input" data-field="${field.id}" data-item="${itemId}" value="${escapeAttr(value)}" />`;
      break;
    case 'color':
      input = `<div class="props-color-wrap">
        <input type="color" class="props-color" data-field="${field.id}" data-item="${itemId}" value="${value}" />
        <span class="props-color-picker" title="Pick color">&#x270F;</span>
      </div>`;
      break;
    case 'gradient':
      input = `<div class="props-color-wrap">
        <div class="props-gradient-swatch" data-field="${field.id}" data-item="${itemId}" style="background:linear-gradient(135deg, ${value}, #000);"></div>
      </div>`;
      break;
    case 'image':
      input = `<div class="props-image-wrap">
        <div class="props-image-thumb">${value ? `<img src="${escapeAttr(value)}" />` : ''}</div>
        <button class="props-image-btn" data-field="${field.id}" data-item="${itemId}">&#x1F5BC; Choose Image</button>
        <button class="props-image-trash" title="Remove image">&#x1F5D1;</button>
        <select class="props-image-fit">
          <option>Fill</option>
          <option>Fit</option>
          <option>Stretch</option>
        </select>
      </div>`;
      break;
    case 'dropdown':
      input = `<select class="props-select" data-field="${field.id}" data-item="${itemId}">
        ${(field.options || []).map((opt) => `<option value="${escapeAttr(opt)}"${opt === value ? ' selected' : ''}>${opt}</option>`).join('')}
      </select>`;
      break;
    case 'toggle':
      input = `<label class="props-toggle">
        <input type="checkbox" data-field="${field.id}" data-item="${itemId}" ${value === 'true' ? 'checked' : ''} />
        <span class="props-toggle-slider"></span>
      </label>`;
      break;
    default:
      input = `<input type="text" class="props-input" data-field="${field.id}" data-item="${itemId}" value="${escapeAttr(value)}" />`;
  }

  return `<div class="props-field-row">
    <label class="${labelClass}">${field.label}</label>
    ${input}
    <span class="props-data-link" title="Data link">&#x25CB;</span>
  </div>`;
}

function renderValuesGrid(fields: TemplateField[], values: Record<string, string>, itemId: string): string {
  return `<table class="props-grid-table">
    <thead><tr><th>Field</th><th>Value</th></tr></thead>
    <tbody>
      ${fields.map((f) => `<tr>
        <td>${f.label}</td>
        <td><input type="text" class="props-grid-input" data-field="${f.id}" data-item="${itemId}" value="${escapeAttr(values[f.id] ?? f.defaultValue)}" /></td>
      </tr>`).join('')}
    </tbody>
  </table>`;
}

// ── Transform Tab ────────────────────────────────────────────
// Reference: assets/screenshots/Zone Layouts.png

function renderTransform(itemId: string): string {
  const item = projectStore.getState().getItem(itemId);
  const currentZoneId = item?.zoneId;
  const preset = zoneStore.getState().getActivePreset();

  const posX = item?.posX ?? 0;
  const posY = item?.posY ?? 0;
  const scaleX = item?.scaleX ?? 100;
  const scaleY = item?.scaleY ?? 100;
  const fitMode = item?.fitMode ?? 'fit';

  // Find current zone name
  let zoneLabel = 'None';
  if (currentZoneId !== undefined) {
    const zone = preset.zones.find((z) => z.id === currentZoneId);
    if (zone) {
      zoneLabel = `${zone.name} (${String(zone.id).padStart(2, '0')})`;
    }
  }

  return `<div class="props-transform">
    <div class="tf-row">
      <label class="tf-label">Position (%)</label>
      <input type="number" class="tf-input tf-pos-x" value="${posX}" step="0.5" />
      <input type="number" class="tf-input tf-pos-y" value="${posY}" step="0.5" />
      <button class="tf-reset-btn tf-reset-transform" title="Reset all transform values">Reset Values</button>
    </div>
    <div class="tf-row">
      <label class="tf-label">Scale (%)</label>
      <input type="number" class="tf-input tf-scale-x" value="${scaleX}" step="1" min="1" max="500" />
      <input type="number" class="tf-input tf-scale-y" value="${scaleY}" step="1" min="1" max="500" />
      <span class="tf-chain${scaleX === scaleY ? ' active' : ''}" title="Lock aspect ratio">&#x1F517;</span>
    </div>
    <div class="tf-row">
      <label class="tf-label">Fit Mode</label>
      <select class="tf-select tf-fit-mode">
        <option value="fill"${fitMode === 'fill' ? ' selected' : ''}>Fill (Crop)</option>
        <option value="fit"${fitMode === 'fit' ? ' selected' : ''}>Fit</option>
        <option value="stretch"${fitMode === 'stretch' ? ' selected' : ''}>Stretch</option>
      </select>
    </div>
    <div class="tf-row">
      <label class="tf-label">Zone Assignment</label>
      <button class="tf-zone-btn" data-item="${itemId}">${zoneLabel}</button>
    </div>
  </div>`;
}

// ── Zone Assignment Popup ────────────────────────────────────

function openZoneAssignmentPopup(itemId: string): void {
  const item = projectStore.getState().getItem(itemId);
  if (!item) return;

  const preset = zoneStore.getState().getActivePreset();
  let selectedZoneId: number | undefined = item.zoneId;

  const popupOverlay = document.createElement('div');
  popupOverlay.className = 'modal-overlay za-overlay';

  const renderPopupContent = () => {
    const checkboxes = preset.zones
      .sort((a, b) => a.id - b.id)
      .map((z) => {
        const checked = selectedZoneId === z.id ? 'checked' : '';
        return `<label class="za-zone-row">
          <input type="checkbox" class="za-checkbox" data-zone-id="${z.id}" ${checked} />
          <span class="za-zone-name">${z.name}</span>
          <span class="za-zone-id">${String(z.id).padStart(2, '0')}</span>
        </label>`;
      }).join('');

    return `
      <div class="za-popup">
        <div class="za-header">
          <span class="za-title">Zone Assignment</span>
          <button class="za-add-btn" title="Add zone">+</button>
        </div>
        <div class="za-diagram">
          ${renderZoneDiagram(preset)}
        </div>
        <div class="za-zone-list">
          ${checkboxes}
        </div>
        <div class="za-footer">
          <button class="za-cancel">Cancel</button>
          <button class="za-okay">Okay</button>
        </div>
      </div>
    `;
  };

  popupOverlay.innerHTML = renderPopupContent();
  document.body.appendChild(popupOverlay);

  // Checkbox behavior: only one zone can be selected (radio-like)
  const attachPopupHandlers = () => {
    popupOverlay.querySelectorAll<HTMLInputElement>('.za-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        const zoneId = parseInt(cb.dataset.zoneId!, 10);
        if (cb.checked) {
          selectedZoneId = zoneId;
          // Uncheck all others
          popupOverlay.querySelectorAll<HTMLInputElement>('.za-checkbox').forEach((other) => {
            if (other !== cb) other.checked = false;
          });
        } else {
          selectedZoneId = undefined;
        }
      });
    });

    // Cancel
    popupOverlay.querySelector('.za-cancel')?.addEventListener('click', () => {
      popupOverlay.remove();
    });

    // Okay — save zone assignment
    popupOverlay.querySelector('.za-okay')?.addEventListener('click', () => {
      projectStore.getState().updateItem(itemId, { zoneId: selectedZoneId });
      eventBus.emit('zone:assignmentChanged', { itemId, zoneId: selectedZoneId });
      popupOverlay.remove();
      render(); // re-render properties to show updated zone
    });

    // Overlay click closes
    popupOverlay.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('za-overlay')) {
        popupOverlay.remove();
      }
    });

    // Escape closes
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        popupOverlay.remove();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  };

  attachPopupHandlers();
}

// ── Event Handlers ───────────────────────────────────────────

function attachHandlers(itemId: string): void {
  // Tab buttons
  panelBody.querySelectorAll<HTMLButtonElement>('.props-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      propertiesStore.getState().setActiveTab(btn.dataset.tab as PropertiesTab);
    });
  });

  // Text/number inputs
  panelBody.querySelectorAll<HTMLInputElement>('.props-input[data-field], .props-grid-input[data-field]').forEach((input) => {
    input.addEventListener('input', () => {
      const fieldId = input.dataset.field!;
      projectStore.getState().updateField(itemId, fieldId, input.value);
      eventBus.emit('properties:fieldChanged', { itemId, fieldId, value: input.value });
    });
  });

  // Color inputs
  panelBody.querySelectorAll<HTMLInputElement>('.props-color[data-field]').forEach((input) => {
    input.addEventListener('input', () => {
      const fieldId = input.dataset.field!;
      projectStore.getState().updateField(itemId, fieldId, input.value);
      eventBus.emit('properties:fieldChanged', { itemId, fieldId, value: input.value });
    });
  });

  // Select inputs
  panelBody.querySelectorAll<HTMLSelectElement>('.props-select[data-field]').forEach((select) => {
    select.addEventListener('change', () => {
      const fieldId = select.dataset.field!;
      projectStore.getState().updateField(itemId, fieldId, select.value);
      eventBus.emit('properties:fieldChanged', { itemId, fieldId, value: select.value });
    });
  });

  // Possession toggle pairs — only one can be true at a time
  const POSSESSION_PAIRS: Record<string, string> = {
    homePossession: 'awayPossession',
    awayPossession: 'homePossession',
  };

  // Toggle inputs
  panelBody.querySelectorAll<HTMLInputElement>('.props-toggle input[data-field]').forEach((input) => {
    input.addEventListener('change', () => {
      const fieldId = input.dataset.field!;
      const value = input.checked ? 'true' : 'false';
      projectStore.getState().updateField(itemId, fieldId, value);
      eventBus.emit('properties:fieldChanged', { itemId, fieldId, value });

      // Mutual exclusion for possession toggles
      const opposite = POSSESSION_PAIRS[fieldId];
      if (opposite && input.checked) {
        projectStore.getState().updateField(itemId, opposite, 'false');
        eventBus.emit('properties:fieldChanged', { itemId, fieldId: opposite, value: 'false' });
        // Update the other checkbox in the UI
        const otherInput = panelBody.querySelector<HTMLInputElement>(`input[data-field="${opposite}"]`);
        if (otherInput) otherInput.checked = false;
      }
    });
  });

  // Image "Choose Image" buttons — open simulated file explorer
  panelBody.querySelectorAll<HTMLButtonElement>('.props-image-btn[data-field]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const fieldId = btn.dataset.field!;
      openFileDialog({
        title: 'Select media',
        startPath: PATHS.LESSON_MEDIA,
        filters: ['All Media (*.png;*.jpg;*.mp4;*.mkv;*.mpg)', 'Image Files (*.png;*.jpg)', 'All Files (*.*)'],
        mode: 'open',
        onConfirm: (file) => {
          if (!file || !file.assetUrl) return;
          projectStore.getState().updateField(itemId, fieldId, file.assetUrl);
          eventBus.emit('properties:fieldChanged', { itemId, fieldId, value: file.assetUrl });
          render(); // re-render to show the thumbnail
        },
      });
    });
  });

  // Image trash buttons — clear the image
  panelBody.querySelectorAll<HTMLButtonElement>('.props-image-trash').forEach((btn) => {
    const wrap = btn.closest('.props-image-wrap');
    const chooseBtn = wrap?.querySelector<HTMLButtonElement>('.props-image-btn[data-field]');
    if (!chooseBtn) return;
    btn.addEventListener('click', () => {
      const fieldId = chooseBtn.dataset.field!;
      projectStore.getState().updateField(itemId, fieldId, '');
      eventBus.emit('properties:fieldChanged', { itemId, fieldId, value: '' });
      render();
    });
  });

  // Position inputs in Transform tab
  const posXInput = panelBody.querySelector<HTMLInputElement>('.tf-pos-x');
  const posYInput = panelBody.querySelector<HTMLInputElement>('.tf-pos-y');

  posXInput?.addEventListener('input', () => {
    const val = parseFloat(posXInput.value) || 0;
    projectStore.getState().updateItem(itemId, { posX: val });
    eventBus.emit('properties:fieldChanged', { itemId, fieldId: 'posX', value: String(val) });
  });

  posYInput?.addEventListener('input', () => {
    const val = parseFloat(posYInput.value) || 0;
    projectStore.getState().updateItem(itemId, { posY: val });
    eventBus.emit('properties:fieldChanged', { itemId, fieldId: 'posY', value: String(val) });
  });

  // Scale inputs in Transform tab
  let chainLocked = panelBody.querySelector('.tf-chain')?.classList.contains('active') ?? false;
  const scaleXInput = panelBody.querySelector<HTMLInputElement>('.tf-scale-x');
  const scaleYInput = panelBody.querySelector<HTMLInputElement>('.tf-scale-y');

  scaleXInput?.addEventListener('input', () => {
    const val = parseFloat(scaleXInput.value) || 100;
    const updates: Partial<ProjectItem> = { scaleX: val };
    if (chainLocked) {
      updates.scaleY = val;
      if (scaleYInput) scaleYInput.value = String(val);
    }
    projectStore.getState().updateItem(itemId, updates);
    eventBus.emit('properties:fieldChanged', { itemId, fieldId: 'scaleX', value: String(val) });
  });

  scaleYInput?.addEventListener('input', () => {
    const val = parseFloat(scaleYInput.value) || 100;
    const updates: Partial<ProjectItem> = { scaleY: val };
    if (chainLocked) {
      updates.scaleX = val;
      if (scaleXInput) scaleXInput.value = String(val);
    }
    projectStore.getState().updateItem(itemId, updates);
    eventBus.emit('properties:fieldChanged', { itemId, fieldId: 'scaleY', value: String(val) });
  });

  // Chain lock toggle (lock aspect ratio)
  panelBody.querySelector('.tf-chain')?.addEventListener('click', () => {
    chainLocked = !chainLocked;
    const chainEl = panelBody.querySelector('.tf-chain');
    chainEl?.classList.toggle('active', chainLocked);
    if (chainLocked && scaleXInput && scaleYInput) {
      // Sync Y to X when locking
      scaleYInput.value = scaleXInput.value;
      const val = parseFloat(scaleXInput.value) || 100;
      projectStore.getState().updateItem(itemId, { scaleY: val });
      eventBus.emit('properties:fieldChanged', { itemId, fieldId: 'scaleY', value: String(val) });
    }
  });

  // Fit Mode select
  panelBody.querySelector<HTMLSelectElement>('.tf-fit-mode')?.addEventListener('change', (e) => {
    const val = (e.target as HTMLSelectElement).value as 'fill' | 'fit' | 'stretch';
    projectStore.getState().updateItem(itemId, { fitMode: val });
    eventBus.emit('properties:fieldChanged', { itemId, fieldId: 'fitMode', value: val });
  });

  // Reset button — reset all transform values to defaults
  panelBody.querySelector('.tf-reset-transform')?.addEventListener('click', () => {
    projectStore.getState().updateItem(itemId, { posX: 0, posY: 0, scaleX: 100, scaleY: 100, fitMode: 'fit' });
    eventBus.emit('properties:fieldChanged', { itemId, fieldId: 'posX', value: '0' });
    render();
  });

  // Zone Assignment button in Transform tab
  panelBody.querySelector<HTMLButtonElement>('.tf-zone-btn')?.addEventListener('click', () => {
    openZoneAssignmentPopup(itemId);
  });
}

function escapeAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
